import { landlordApi } from './api';
import { getAppMarket } from './MarketService';

// Default certificate types (used as fallback)
export const DEFAULT_CERT_TYPES = [
    { id: 'gas-safety', key: 'Gas Safety', label: 'Gas Safety Certificate', icon: 'fa-fire', color: 'orange' },
    { id: 'eicr', key: 'Electrical (EICR)', label: 'Electrical Installation Condition Report', icon: 'fa-bolt', color: 'blue' },
    { id: 'epc', key: 'Energy (EPC)', label: 'Energy Performance Certificate', icon: 'fa-leaf', color: 'green' },
    { id: 'landlord-insurance', key: 'Landlord Insurance', label: 'Landlord Insurance Policy', icon: 'fa-shield-alt', color: 'purple' },
    { id: 'tenancy-agreement', key: 'Tenancy Agreement', label: 'Tenancy Agreement', icon: 'fa-file-contract', color: 'indigo' },
    { id: 'hmo-license', key: 'HMO License', label: 'House in Multiple Occupation License', icon: 'fa-home', color: 'pink' },
    { id: 'smoke-alarms', key: 'Smoke Alarms', label: 'Smoke Alarm Certificate', icon: 'fa-exclamation-triangle', color: 'red' },
    { id: 'carbon-monoxide', key: 'Carbon Monoxide', label: 'Carbon Monoxide Alarm Certificate', icon: 'fa-wind', color: 'gray' },
    { id: 'pat', key: 'Portable Appliance Testing (PAT)', label: 'PAT Testing Certificate', icon: 'fa-plug', color: 'yellow' },
    { id: 'legionella', key: 'Legionella Risk Assessment', label: 'Legionella Risk Assessment', icon: 'fa-tint', color: 'blue' },
    { id: 'deposit-protection', key: 'Deposit Protection', label: 'Deposit Protection Certificate', icon: 'fa-piggy-bank', color: 'emerald' }
];

// F-307: US document set for the Texas market — up to 5 user-editable slots
// with required/optional labelling (digital vault).
export const US_DEFAULT_CERT_TYPES = [
    { id: 'lease-agreement', key: 'Lease Agreement', label: 'Texas Lease Agreement', icon: 'fa-file-contract', color: 'indigo', required: true },
    { id: 'lead-paint-disclosure', key: 'Lead Paint Disclosure', label: 'Lead Paint Disclosure (pre-1978 homes)', icon: 'fa-paint-roller', color: 'amber', required: true },
    { id: 'registration-certificate', key: 'Registration Certificate', label: 'Municipal Registration Certificate', icon: 'fa-clipboard-check', color: 'emerald', required: true },
    { id: 'hvac-service', key: 'HVAC Service Record', label: 'HVAC / A/C Service Record', icon: 'fa-snowflake', color: 'sky', required: true },
    { id: 'condition-report', key: 'Condition Report', label: 'Property Condition Report', icon: 'fa-house-circle-check', color: 'purple', required: false },
];

const isUsShape = (types: any[]) =>
    Array.isArray(types) && types.some((t) => /lease agreement/i.test(String(t?.key || '')));

const isUkShape = (types: any[]) =>
    Array.isArray(types) && types.some((t) => String(t?.key || '') === 'Gas Safety');

// Texas market: a UK-default-shaped list (saved or cached before the market
// switch) is swapped for the US set; genuinely US-shaped or user-customised
// lists pass through untouched.
export const marketTypes = (types: any[]): any[] => {
    if (getAppMarket() !== 'texas') return types;
    if (!Array.isArray(types)) return US_DEFAULT_CERT_TYPES;
    return isUkShape(types) && !isUsShape(types) ? US_DEFAULT_CERT_TYPES : types;
};

export const getDefaultCertTypes = (): any[] =>
    getAppMarket() === 'texas' ? US_DEFAULT_CERT_TYPES : DEFAULT_CERT_TYPES;

// Cache to avoid repeated API calls
let cachedTypes: any[] | null = null;

// Get certificate types from API
export const getCertificateTypes = async (): Promise<any[]> => {
    // Return cache if available
    if (cachedTypes) {
        return cachedTypes;
    }

    try {
        const response = await landlordApi.get('/api/settings/certificate-types');
        const types = marketTypes(response.data.data);
        cachedTypes = types;
        localStorage.setItem('cert_types_cache', JSON.stringify(types));
        console.log('[CertTypes] Loaded from API:', cachedTypes!.length, 'types');
        return cachedTypes!;
    } catch (error) {
        console.error('[CertTypes] Failed to load from API:', error);
        // Fallback to defaults
        return getDefaultCertTypes();
    }
};

// Get certificate types synchronously (for components that need immediate access)
// This uses localStorage as a temporary cache until API loads
export const getCertificateTypesSync = () => {
    if (cachedTypes) {
        return marketTypes(cachedTypes);
    }

    // Check localStorage cache
    try {
        const stored = localStorage.getItem('cert_types_cache');
        if (stored) {
            return marketTypes(JSON.parse(stored));
        }
    } catch (error) {
        console.error('[CertTypes] localStorage error:', error);
    }

    return getDefaultCertTypes();
};

// Save certificate types to API
export const saveCertificateTypes = async (types: any[]) => {
    try {
        const response = await landlordApi.put('/api/settings/certificate-types', {
            certificateTypes: types
        });

        // Update cache
        cachedTypes = types;
        localStorage.setItem('cert_types_cache', JSON.stringify(types));

        console.log('[CertTypes] Saved to API:', types.length, 'types');
        return response.data;
    } catch (error) {
        console.error('[CertTypes] Failed to save:', error);
        throw error;
    }
};

// Reset to defaults
export const resetCertificateTypes = async () => {
    try {
        const response = await landlordApi.post('/api/settings/certificate-types/reset');

        // Update cache
        const types = getDefaultCertTypes();
        cachedTypes = types;
        localStorage.setItem('cert_types_cache', JSON.stringify(types));

        console.log('[CertTypes] Reset to defaults:', types.length, 'types');
        return response.data;
    } catch (error) {
        console.error('[CertTypes] Failed to reset:', error);
        throw error;
    }
};
